import { useMutation, useQuery, useQueryClient } from "react-query";
import Axios, { AxiosResponse, AxiosError } from "axios";
import { APIError, User, type UserPagination, type UserPaginationParams } from "src/types";
import { usePasswordResetMutation } from "./usePasswordResetMutation";
import { userKeys } from "src/query-keys";

export type UserMutationData = Pick<User, "firstName" | "lastName" | "email" | "role">;

/**
 * Deserialize the json format into a Javascript object
 */
function deserialize(data: User): User {
  return {
    ...data,
    dateCreated: new Date(data.dateCreated),
    dateLastLoginLinkSent: data.dateLastLoginLinkSent ? new Date(data.dateLastLoginLinkSent) : new Date(),
  };
}

export function useUsers(userPagination: UserPaginationParams) {
  async function queryFn() {
    const url = "/planning-api/users";
    const response = await Axios.get(url, {
      params: {
        limit: userPagination.limit,
        offset: userPagination.offset,
        sort: userPagination.orderBy,
        sortDirection: userPagination.orderDirection.toUpperCase(),
      },
    });
    if (response.status === 200) return { ...response.data, data: response.data.data.map(deserialize) };
    return response.data;
  }

  return useQuery<UserPagination, APIError>(
    ["users", userPagination.limit, userPagination.offset, userPagination.orderBy, userPagination.orderDirection],
    queryFn,
    {
      keepPreviousData: true,
      suspense: false,
    }
  );
}

export function useUser(userId: number | string) {
  async function queryFn(userId: number | string) {
    const response = await Axios.get(`/planning-api/users/${userId}`);
    return deserialize(response.data);
  }

  return useQuery<User, APIError>(userKeys.detail(userId), () => queryFn(userId));
}

export function useUserCreateMutation() {
  const queryClient = useQueryClient();

  async function mutationFn(data: UserMutationData): Promise<User> {
    const url = "/planning-api/users";
    const response = await Axios.post<any, AxiosResponse<User>>(url, data);
    return response.data;
    // return { id: response.data.id, ...data };
  }
  const loginLinkRequestMutation = useLoginLinkRequestMutation();

  return useMutation<User, AxiosError<APIError[]>, UserMutationData, unknown>(
    (values: UserMutationData) => mutationFn(values),
    {
      onSuccess: async (user) => {
        if (user.enabled) await loginLinkRequestMutation.mutateAsync(user.authUserId);
        return;
      },
      onSettled: () => queryClient.invalidateQueries(userKeys.all),
    }
  );
}

export function useLoginLinkRequestMutation() {
  const queryClient = useQueryClient();

  return usePasswordResetMutation({
    onSuccess: () => queryClient.invalidateQueries(userKeys.all),
  });
}

export function useUserUpdateMutation(userId: number | string) {
  const queryClient = useQueryClient();

  async function mutationFn(userId: number | string, data: UserMutationData): Promise<UserMutationData> {
    const url = `/planning-api/users/${userId}`;
    const response = await Axios.put<UserMutationData, AxiosResponse<UserMutationData>>(url, data);
    return response.data;
  }

  return useMutation<UserMutationData, AxiosError<APIError[]>, UserMutationData, unknown>(
    (values: UserMutationData) => {
      return mutationFn(userId, values);
    },
    {
      onSuccess: async (updatedUser, variables) => {
        // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
        await queryClient.cancelQueries(["users"]);

        // Optimistically update to the new value
        queryClient.setQueryData(userKeys.detail(userId), () => updatedUser);
        queryClient.setQueryData(userKeys.detail(userId), (oldData: User[] | undefined) => {
          if (!oldData) return [];
          return [...oldData].map((item) => {
            if (item.id === userId) return { ...item, ...variables };
            return item;
          });
        });
      },
      onSettled: async () => {
        await queryClient.invalidateQueries(userKeys.all);
      },
    }
  );
}

export function useUserDeleteMutation() {
  const queryClient = useQueryClient();

  async function mutationFn(userId: number | string): Promise<void> {
    const url = `/planning-api/users/${userId}`;
    return await Axios.delete(url);
  }

  return useMutation<void, AxiosError<APIError[]>, number | string, unknown>(
    (userId: number | string) => mutationFn(userId),
    { onSettled: () => queryClient.invalidateQueries(userKeys.all) }
  );
}
